/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useRef } from 'react';
import { 
  Award, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  FileSpreadsheet, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  FileText, 
  Paperclip, 
  History, 
  Trash2, 
  Edit3, 
  ExternalLink, 
  Building2, 
  Eye, 
  Upload, 
  X, 
  Sparkles, 
  ShieldCheck, 
  Calendar, 
  Bell, 
  Layers, 
  FileCheck2,
  Check,
  ChevronDown,
  LayoutGrid,
  List,
  FolderArchive,
  Image as ImageIcon
} from 'lucide-react';
import { CertificateRecord, CertificateAttachment, CertificateHistoryEntry, CostCenter } from '../types.ts';
import { CertExcelExportModal } from './CertExcelExportModal.tsx';
import { CertFilesHistoryModal } from './CertFilesHistoryModal.tsx';
import { exportCertificatesToExcel, getCertificateValidityInfo } from '../utils/certificationsExcelExporter.ts';

interface CertificationsManagerProps {
  activeCostCenter?: CostCenter | null;
  costCenters?: CostCenter[];
  certificates: CertificateRecord[];
  onSaveCertificates: (updated: CertificateRecord[]) => void;
}

export const CertificationsManager: React.FC<CertificationsManagerProps> = ({
  activeCostCenter,
  costCenters = [],
  certificates = [],
  onSaveCertificates
}) => {
  // Navigation & View States
  const [viewMode, setViewMode] = useState<'grid' | 'table' | 'files'>('grid');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'expiring_30' | 'expired' | 'valid' | 'in_audit'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [notificationToast, setNotificationToast] = useState<string | null>(null);

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isExcelExportModalOpen, setIsExcelExportModalOpen] = useState<boolean>(false);
  const [exportModalInitialPreset, setExportModalInitialPreset] = useState<'all' | 'expiring_30' | 'expired' | 'valid'>('expiring_30');
  const [selectedCertForHistory, setSelectedCertForHistory] = useState<CertificateRecord | null>(null);
  const [editingCert, setEditingCert] = useState<CertificateRecord | null>(null);

  // Form State for Create / Edit
  const [formData, setFormData] = useState<Partial<CertificateRecord>>({
    name: '',
    code: '',
    issuer: '',
    category: 'calidad',
    issueDate: new Date().toISOString().split('T')[0],
    validUntil: '',
    scope: '',
    status: 'activo',
    leadAuditor: '',
    auditType: 'seguimiento',
    notes: '',
    attachments: []
  });
  const [stagedFiles, setStagedFiles] = useState<CertificateAttachment[]>([]);
  const newFileInputRef = useRef<HTMLInputElement | null>(null);

  // Enrich certificates with validity calculations
  const enrichedCertificates = useMemo(() => {
    return certificates.map(cert => {
      const validity = getCertificateValidityInfo(cert.validUntil, cert.status, 30);
      return {
        ...cert,
        validity
      };
    });
  }, [certificates]);

  // Filtered Certificates
  const filteredCertificates = useMemo(() => {
    return enrichedCertificates.filter(cert => {
      // 1. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const match = 
          (cert.name && cert.name.toLowerCase().includes(q)) ||
          (cert.code && cert.code.toLowerCase().includes(q)) ||
          (cert.issuer && cert.issuer.toLowerCase().includes(q)) ||
          (cert.scope && cert.scope.toLowerCase().includes(q)) ||
          (cert.leadAuditor && cert.leadAuditor.toLowerCase().includes(q));
        if (!match) return false;
      }

      // 2. Category Filter
      if (categoryFilter !== 'all' && cert.category !== categoryFilter) {
        return false;
      }

      // 3. Status Filter
      if (statusFilter === 'expiring_30') {
        return cert.validity.daysRemaining >= 0 && cert.validity.daysRemaining <= 30 && cert.status !== 'en_auditoria';
      }
      if (statusFilter === 'expired') {
        return cert.validity.daysRemaining < 0 && cert.status !== 'en_auditoria';
      }
      if (statusFilter === 'valid') {
        return cert.validity.daysRemaining > 30 && cert.validity.computedStatus === 'vigente';
      }
      if (statusFilter === 'in_audit') {
        return cert.status === 'en_auditoria' || cert.status === 'en_renovacion';
      }

      return true;
    });
  }, [enrichedCertificates, searchQuery, categoryFilter, statusFilter]);

  // Key Metrics
  const metrics = useMemo(() => {
    const total = enrichedCertificates.length;
    const expiring30 = enrichedCertificates.filter(c => c.validity.daysRemaining >= 0 && c.validity.daysRemaining <= 30 && c.status !== 'en_auditoria').length;
    const expired = enrichedCertificates.filter(c => c.validity.daysRemaining < 0 && c.status !== 'en_auditoria').length;
    const valid = enrichedCertificates.filter(c => c.validity.daysRemaining > 30 && c.validity.computedStatus === 'vigente').length;
    const inAudit = enrichedCertificates.filter(c => c.status === 'en_auditoria' || c.status === 'en_renovacion').length;
    const totalFiles = enrichedCertificates.reduce((acc, c) => acc + (c.attachments?.length || 0), 0);

    return { total, expiring30, expired, valid, inAudit, totalFiles };
  }, [enrichedCertificates]);

  // Urgent expiring certifications list for banner
  const urgentExpiringList = useMemo(() => {
    return enrichedCertificates.filter(c => c.validity.daysRemaining >= 0 && c.validity.daysRemaining <= 30 && c.status !== 'en_auditoria');
  }, [enrichedCertificates]);

  // Quick 1-Click Export for 30-Day Expiring
  const handleQuickExport30Days = async () => {
    const targetCerts = urgentExpiringList.length > 0 
      ? urgentExpiringList 
      : enrichedCertificates.filter(c => c.validity.daysRemaining <= 30);

    if (targetCerts.length === 0) {
      alert('No se registran certificaciones próximas a vencer en los próximos 30 días.');
      return;
    }

    try {
      const timestamp = new Date().toISOString().split('T')[0];
      await exportCertificatesToExcel(targetCerts, {
        fileName: `Certificaciones_Proximas_Vencer_30Dias_${timestamp}.xlsx`,
        reportTitle: 'REPORTE CRÍTICO: CERTIFICACIONES Y AUDITORÍAS POR RENOVAR (≤ 30 DÍAS)',
        filterDescription: 'Ventana crítica de vencimiento a 30 días para trámite de renovación oportuna',
        costCenterName: activeCostCenter?.name || 'Todas las Sedes',
        generatedBy: 'Coordinación de Calidad & HSEQ',
        includeExpiringOnlySheet: true,
        includeAttachmentsHistorySheet: true,
        highlightExpiringDays: 30
      });
      showToast('¡Descarga generada con éxito! Excel con formato profesional, logo y alertas a 30 días.');
    } catch (e) {
      console.error('Error quick export', e);
      alert('Error al generar la descarga en Excel.');
    }
  };

  const showToast = (msg: string) => {
    setNotificationToast(msg);
    setTimeout(() => {
      setNotificationToast(null);
    }, 4500);
  };

  // Simulate Notification Dispatch (Push / Email / In-App) for 30 days
  const handleDispatch30DayNotifications = () => {
    if (urgentExpiringList.length === 0) {
      showToast('No hay certificaciones en ventana de 30 días pendientes de notificar.');
      return;
    }

    // Play subtle audio chime if supported
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15); // A5
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch {
      // Audio context ignored if not supported
    }

    // Update notification flag in records
    const updated = certificates.map(cert => {
      const isUrgent = urgentExpiringList.some(u => u.id === cert.id);
      if (isUrgent) {
        const histEntry: CertificateHistoryEntry = {
          id: `hist_notif_${Date.now()}_${Math.random().toString(36).substring(2, 5)}`,
          date: new Date().toISOString(),
          type: 'observacion',
          title: 'Notificación de Vencimiento Enviada (Alerta 30 Días)',
          description: `Se despachó aviso preventivo al equipo HSEQ y auditores responsables para programar auditoría de recertificación.`,
          user: 'Sistema Automatizado de Alertas HSEQ'
        };
        return {
          ...cert,
          notification30DaysSent: true,
          lastNotificationDate: new Date().toISOString(),
          history: [...(cert.history || []), histEntry]
        };
      }
      return cert;
    });

    onSaveCertificates(updated);
    showToast(`🔔 ¡Notificaciones de alerta (30 días) enviadas con éxito para ${urgentExpiringList.length} certificación(es)!`);
  };

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setEditingCert(null);
    setStagedFiles([]);
    setFormData({
      name: '',
      code: '',
      issuer: '',
      category: 'calidad',
      issueDate: new Date().toISOString().split('T')[0],
      validUntil: '',
      scope: '',
      status: 'activo',
      leadAuditor: '',
      auditType: 'seguimiento',
      notes: '',
      costCenterId: activeCostCenter?.id,
      costCenterName: activeCostCenter?.name
    });
    setIsCreateModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (cert: CertificateRecord) => {
    setEditingCert(cert);
    setStagedFiles(cert.attachments || []);
    setFormData({
      ...cert
    });
    setIsCreateModalOpen(true);
  };

  // Detect file type
  const detectFileType = (fileName: string): 'pdf' | 'word' | 'powerpoint' | 'image' | 'excel' | 'other' => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['ppt', 'pptx', 'pps', 'ppsx'].includes(ext)) return 'powerpoint';
    if (['jpg', 'jpeg', 'png', 'webp', 'svg', 'gif'].includes(ext)) return 'image';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
    return 'other';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle stage files during modal creation
  const handleStageFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: CertificateAttachment[] = [];

    Array.from(files).forEach((file: File, idx) => {
      const reader = new FileReader();
      const detected = detectFileType(file.name);

      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        const newAtt: CertificateAttachment = {
          id: `att_${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 6)}`,
          name: file.name,
          fileType: detected,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          sizeFormatted: formatFileSize(file.size),
          dataUrl: dataUrl,
          uploadedAt: new Date().toISOString(),
          uploadedBy: 'Auditor HSEQ',
          versionNumber: stagedFiles.length + newAttachments.length + 1,
          notes: 'Documento adjunto en registro inicial'
        };

        newAttachments.push(newAtt);

        if (newAttachments.length === files.length) {
          setStagedFiles(prev => [...prev, ...newAttachments]);
        }
      };

      reader.readAsDataURL(file);
    });

    if (newFileInputRef.current) {
      newFileInputRef.current.value = '';
    }
  };

  // Remove staged file
  const handleRemoveStagedFile = (attId: string) => {
    setStagedFiles(prev => prev.filter(f => f.id !== attId));
  };

  // Save Certificate
  const handleSaveForm = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name?.trim() || !formData.code?.trim()) {
      alert('Por favor capture el nombre de la certificación y el código/norma.');
      return;
    }

    if (!formData.validUntil) {
      alert('Por favor especifique la Fecha de Vencimiento o Próxima Auditoría.');
      return;
    }

    if (editingCert) {
      // Edit existing
      const updatedList = certificates.map(c => {
        if (c.id === editingCert.id) {
          const histEntry: CertificateHistoryEntry = {
            id: `hist_${Date.now()}`,
            date: new Date().toISOString(),
            type: 'observacion',
            title: 'Actualización de Datos de Certificación',
            description: `Se actualizaron parámetros de la norma ${formData.code} con fecha de vigencia ${formData.validUntil}.`,
            user: 'Coordinación HSEQ'
          };

          return {
            ...c,
            ...formData,
            attachments: stagedFiles,
            history: [...(c.history || []), histEntry],
            updatedAt: new Date().toISOString()
          } as CertificateRecord;
        }
        return c;
      });

      onSaveCertificates(updatedList);
      showToast(`Certificación "${formData.name}" actualizada con éxito.`);
    } else {
      // Create new
      const newCertId = `cert_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const creationHist: CertificateHistoryEntry = {
        id: `hist_init_${Date.now()}`,
        date: new Date().toISOString(),
        type: 'creacion',
        title: 'Registro Inicial de Certificación / Auditoría',
        description: `Se dio de alta la certificación ${formData.code} emitida por ${formData.issuer || 'Organismo Acreditado'} con vigencia hasta ${formData.validUntil}.`,
        user: 'Coordinación HSEQ'
      };

      const newRecord: CertificateRecord = {
        id: newCertId,
        name: formData.name || 'Certificación HSEQ',
        code: formData.code || 'ISO-9001',
        issuer: formData.issuer || 'Organismo Certificador Acreditado',
        category: formData.category || 'calidad',
        issueDate: formData.issueDate || new Date().toISOString().split('T')[0],
        validUntil: formData.validUntil,
        scope: formData.scope || 'Alcance general en planta',
        status: (formData.status as any) || 'activo',
        costCenterId: activeCostCenter?.id || 'CC-MAZDA-BAJIO',
        costCenterName: activeCostCenter?.name || 'Planta Operativa',
        leadAuditor: formData.leadAuditor || 'Auditor Líder Certificado',
        auditType: formData.auditType || 'seguimiento',
        notes: formData.notes || '',
        attachments: stagedFiles,
        history: [creationHist],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      onSaveCertificates([newRecord, ...certificates]);
      showToast(`Nueva certificación "${newRecord.name}" registrada con éxito.`);
    }

    setIsCreateModalOpen(false);
  };

  // Delete Certificate
  const handleDeleteCert = (certId: string, certName: string) => {
    if (!window.confirm(`¿Está seguro de eliminar permanentemente la certificación "${certName}" y todo su expediente de archivos?`)) {
      return;
    }
    const updated = certificates.filter(c => c.id !== certId);
    onSaveCertificates(updated);
    showToast('Certificación eliminada.');
  };

  // Download Single Attached File
  const handleDownloadAttachment = (att: CertificateAttachment, certCode: string) => {
    if (!att.dataUrl) {
      alert('Archivo no disponible para descarga.');
      return;
    }
    const a = document.createElement('a');
    a.href = att.dataUrl;
    a.download = att.name || `${certCode}_documento`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Helper file icon
  const renderFileBadge = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-red-950/80 text-red-300 border border-red-700/50">PDF</span>;
      case 'word':
        return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-sky-950/80 text-sky-300 border border-sky-700/50">DOCX</span>;
      case 'powerpoint':
        return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-orange-950/80 text-orange-300 border border-orange-700/50">PPTX</span>;
      case 'image':
        return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-emerald-950/80 text-emerald-300 border border-emerald-700/50">IMG</span>;
      case 'excel':
        return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-green-950/80 text-green-300 border border-green-700/50">XLSX</span>;
      default:
        return <span className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-slate-800 text-slate-300">DOC</span>;
    }
  };

  return (
    <div className="space-y-5">
      
      {/* NOTIFICATION TOAST */}
      {notificationToast && (
        <div className="fixed bottom-6 right-6 z-60 bg-gradient-to-r from-slate-900 to-amber-950 border border-amber-500/60 p-4 rounded-2xl shadow-2xl text-white text-xs flex items-center gap-3 animate-bounce">
          <div className="w-8 h-8 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold">
            <Bell className="w-4 h-4" />
          </div>
          <div>
            <h5 className="font-bold text-amber-300 text-xs">Aviso del Sistema HSEQ</h5>
            <p className="text-slate-200 text-[11px]">{notificationToast}</p>
          </div>
        </div>
      )}

      {/* KPI DASHBOARD */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Total */}
        <div className="p-4 rounded-2xl bg-[#16171b] border border-[#272935] flex flex-col justify-between hover:border-slate-600 transition-colors">
          <div className="flex items-center justify-between text-slate-400">
            <span className="text-[11px] font-bold uppercase tracking-wider">Total Acreditadas</span>
            <Award className="w-4 h-4 text-slate-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold font-mono text-white">{metrics.total}</span>
            <span className="text-[10px] text-slate-400 block mt-0.5">Normas & Auditorías</span>
          </div>
        </div>

        {/* Vigentes */}
        <div className="p-4 rounded-2xl bg-[#16171b] border border-emerald-500/30 flex flex-col justify-between hover:border-emerald-500 transition-colors">
          <div className="flex items-center justify-between text-emerald-400">
            <span className="text-[11px] font-bold uppercase tracking-wider">Vigentes (&gt;30d)</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold font-mono text-emerald-300">{metrics.valid}</span>
            <span className="text-[10px] text-emerald-500/80 block mt-0.5">Vigencia Plena</span>
          </div>
        </div>

        {/* Proximas a vencer (30 dias) */}
        <div 
          onClick={() => {
            setStatusFilter('expiring_30');
          }}
          className={`p-4 rounded-2xl border flex flex-col justify-between cursor-pointer transition-all ${
            metrics.expiring30 > 0
              ? 'bg-amber-950/30 border-amber-500/60 hover:bg-amber-950/50 ring-1 ring-amber-500/30 shadow-lg shadow-amber-950/40'
              : 'bg-[#16171b] border-[#272935] hover:border-amber-500/40'
          }`}
        >
          <div className="flex items-center justify-between text-amber-400">
            <span className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
              <span>Por Vencer (≤30d)</span>
              {metrics.expiring30 > 0 && <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />}
            </span>
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold font-mono text-amber-400">{metrics.expiring30}</span>
            <span className="text-[10px] text-amber-400/80 block mt-0.5">Requieren Renovación</span>
          </div>
        </div>

        {/* Vencidas */}
        <div 
          onClick={() => {
            setStatusFilter('expired');
          }}
          className={`p-4 rounded-2xl border flex flex-col justify-between cursor-pointer transition-all ${
            metrics.expired > 0
              ? 'bg-red-950/30 border-red-500/60 hover:bg-red-950/50'
              : 'bg-[#16171b] border-[#272935] hover:border-red-500/40'
          }`}
        >
          <div className="flex items-center justify-between text-red-400">
            <span className="text-[11px] font-bold uppercase tracking-wider">Vencidas</span>
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold font-mono text-red-400">{metrics.expired}</span>
            <span className="text-[10px] text-red-400/80 block mt-0.5">Acción Extraordinaria</span>
          </div>
        </div>

        {/* En Auditoría */}
        <div className="p-4 rounded-2xl bg-[#16171b] border border-sky-500/30 flex flex-col justify-between hover:border-sky-500 transition-colors">
          <div className="flex items-center justify-between text-sky-400">
            <span className="text-[11px] font-bold uppercase tracking-wider">En Auditoría</span>
            <Layers className="w-4 h-4 text-sky-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold font-mono text-sky-300">{metrics.inAudit}</span>
            <span className="text-[10px] text-sky-400/80 block mt-0.5">Proceso en Curso</span>
          </div>
        </div>

        {/* Total Archivos Adjuntos */}
        <div 
          onClick={() => setViewMode('files')}
          className="p-4 rounded-2xl bg-[#16171b] border border-indigo-500/30 flex flex-col justify-between hover:border-indigo-500 transition-colors cursor-pointer"
        >
          <div className="flex items-center justify-between text-indigo-400">
            <span className="text-[11px] font-bold uppercase tracking-wider">Archivos Digitales</span>
            <Paperclip className="w-4 h-4 text-indigo-400" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-bold font-mono text-indigo-300">{metrics.totalFiles}</span>
            <span className="text-[10px] text-indigo-400/80 block mt-0.5">PDF, Word, PPTX, Img</span>
          </div>
        </div>
      </div>

      {/* URGENT 30-DAY ALERT BANNER */}
      {metrics.expiring30 > 0 && (
        <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-amber-950/70 via-[#221a10] to-[#171821] border border-amber-500/50 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/40 flex items-center justify-center shrink-0 mt-0.5">
              <Clock className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-white text-sm">
                  Alerta Preventiva de Renovación: {metrics.expiring30} Certificación(es) vencen en ≤ 30 días
                </h4>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-black font-mono">
                  ACCIONAR RECERTIFICACIÓN
                </span>
              </div>
              <p className="text-xs text-amber-200/80 mt-1 max-w-2xl leading-relaxed">
                Se detectaron acreditaciones y auditorías a 30 días de su fecha límite. Descargue el listado en Excel con logotipo y envíe las notificaciones preventivas al equipo de Calidad y HSEQ.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <button
              onClick={handleDispatch30DayNotifications}
              className="px-3.5 py-2 rounded-xl bg-[#2b2215] hover:bg-[#3d2f1b] text-amber-300 border border-amber-500/40 text-xs font-bold flex items-center gap-2 cursor-pointer transition-colors shadow-md"
            >
              <Bell className="w-3.5 h-3.5" />
              <span>Despachar Notificaciones (30d)</span>
            </button>

            <button
              onClick={handleQuickExport30Days}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-xs font-bold flex items-center gap-2 cursor-pointer transition-all shadow-lg shadow-amber-950/60"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Descargar Lista en Excel (.xlsx)</span>
            </button>
          </div>
        </div>
      )}

      {/* ACTION & CONTROLS TOOLBAR */}
      <div className="p-4 sm:p-5 rounded-2xl bg-[#16171b] border border-[#282932] space-y-4">
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              <span>Panel de Certificaciones, Auditorías y Acreditaciones HSEQ</span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Gestión de acreditaciones ISO, STPS, Protección Civil, auditorías de clientes y expediente digital de evidencias.
            </p>
          </div>

          {/* Master Actions Buttons */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-[#101115] p-1 rounded-xl border border-[#272935]">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  viewMode === 'grid'
                    ? 'bg-[#252836] text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Vista Cuadrícula de Tarjetas"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Tarjetas</span>
              </button>

              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  viewMode === 'table'
                    ? 'bg-[#252836] text-white shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Vista Tabla Detallada"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Tabla</span>
              </button>

              <button
                onClick={() => setViewMode('files')}
                className={`p-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer ${
                  viewMode === 'files'
                    ? 'bg-[#252836] text-indigo-300 shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
                title="Histórico Central de Archivos"
              >
                <FolderArchive className="w-4 h-4" />
                <span className="hidden sm:inline">Archivos ({metrics.totalFiles})</span>
              </button>
            </div>

            {/* Excel Export Button */}
            <button
              onClick={() => {
                setExportModalInitialPreset('all');
                setIsExcelExportModalOpen(true);
              }}
              className="px-4 py-2 rounded-xl bg-[#1e202a] hover:bg-[#282b3a] text-amber-300 border border-amber-500/40 font-bold text-xs flex items-center gap-2 cursor-pointer shadow-md transition-all"
            >
              <FileSpreadsheet className="w-4 h-4 text-amber-400" />
              <span>Exportar a Excel (Filtros & Logo)</span>
            </button>

            {/* Register New Certification */}
            <button
              onClick={handleOpenCreateModal}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold text-xs flex items-center gap-2 cursor-pointer shadow-lg shadow-red-950/50 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Registrar Certificación / Auditoría</span>
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-[#232532]">
          
          {/* Search Box */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar norma, código, emisor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#101115] border border-[#272935] rounded-xl pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full bg-[#101115] border border-[#272935] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
            >
              <option value="all">Estatus: Todas las Certificaciones ({metrics.total})</option>
              <option value="expiring_30">⚠️ Próximas a Vencer (≤ 30 Días) [{metrics.expiring30}]</option>
              <option value="expired">🔴 Auditorías Vencidas [{metrics.expired}]</option>
              <option value="valid">🟢 Vigentes (&gt; 30 Días) [{metrics.valid}]</option>
              <option value="in_audit">🔵 En Auditoría / Renovación [{metrics.inAudit}]</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full bg-[#101115] border border-[#272935] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
            >
              <option value="all">Categoría: Todas las Áreas</option>
              <option value="calidad">Calidad (ISO 9001 / IATF 16949)</option>
              <option value="ambiental">Medio Ambiente (ISO 14001)</option>
              <option value="seguridad">Seguridad y Salud (ISO 45001 / STPS)</option>
              <option value="proteccion_civil">Protección Civil & Dictámenes</option>
              <option value="metrologia">Metrología & Calibración</option>
              <option value="auditoria_cliente">Auditorías de Cliente (VDA / OEM)</option>
            </select>
          </div>

          {/* Reset button */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-400 text-[11px]">
              Mostrando <strong className="text-white">{filteredCertificates.length}</strong> de {enrichedCertificates.length}
            </span>
            {(searchQuery || statusFilter !== 'all' || categoryFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('all');
                  setCategoryFilter('all');
                }}
                className="text-amber-400 hover:text-amber-300 font-bold text-[11px] underline cursor-pointer"
              >
                Limpiar Filtros
              </button>
            )}
          </div>

        </div>

      </div>

      {/* CONTENT VIEWS */}
      
      {/* 1. GRID CARDS VIEW */}
      {viewMode === 'grid' && (
        <div>
          {filteredCertificates.length === 0 ? (
            <div className="text-center py-16 px-4 rounded-2xl bg-[#16171b] border border-[#282932] space-y-3">
              <Award className="w-12 h-12 text-slate-600 mx-auto" />
              <h4 className="text-sm font-bold text-white">No se encontraron certificaciones ni auditorías</h4>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                Ajuste los criterios de búsqueda o capture una nueva certificación acreditada.
              </p>
              <button
                onClick={handleOpenCreateModal}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold cursor-pointer inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Registrar Primera Certificación</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCertificates.map(cert => {
                const v = cert.validity;
                const attachmentsCount = cert.attachments?.length || 0;

                return (
                  <div
                    key={cert.id}
                    className={`rounded-2xl border p-5 flex flex-col justify-between gap-4 transition-all relative group bg-[#16171b] ${
                      v.computedStatus === 'vencido'
                        ? 'border-red-500/60 shadow-lg shadow-red-950/20'
                        : v.computedStatus === 'proximo_vencer'
                        ? 'border-amber-500/60 shadow-lg shadow-amber-950/20 ring-1 ring-amber-500/30'
                        : 'border-[#282932] hover:border-slate-500'
                    }`}
                  >
                    {/* Top Row: Code & Status */}
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-mono text-xs font-bold text-amber-400 bg-amber-950/60 border border-amber-700/60 px-2.5 py-0.5 rounded-lg shadow-xs">
                            {cert.code}
                          </span>
                          <span className="text-[10px] font-mono text-slate-400 uppercase bg-[#1e2029] px-2 py-0.5 rounded border border-[#313444]">
                            {cert.category}
                          </span>
                        </div>

                        {/* Status Badge */}
                        <div>
                          {v.computedStatus === 'vencido' && (
                            <span className="text-[10px] font-bold text-red-300 bg-red-950/80 border border-red-700 px-2 py-0.5 rounded-md flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3 text-red-400" />
                              Vencida ({Math.abs(v.daysRemaining)}d)
                            </span>
                          )}
                          {v.computedStatus === 'proximo_vencer' && (
                            <span className="text-[10px] font-bold text-amber-300 bg-amber-950/80 border border-amber-700 px-2 py-0.5 rounded-md flex items-center gap-1 animate-pulse">
                              <Clock className="w-3 h-3 text-amber-400" />
                              Vence en {v.daysRemaining}d
                            </span>
                          )}
                          {v.computedStatus === 'vigente' && (
                            <span className="text-[10px] font-bold text-emerald-300 bg-emerald-950/80 border border-emerald-700 px-2 py-0.5 rounded-md flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                              Vigente ({v.daysRemaining}d)
                            </span>
                          )}
                          {v.computedStatus === 'en_auditoria' && (
                            <span className="text-[10px] font-bold text-sky-300 bg-sky-950/80 border border-sky-700 px-2 py-0.5 rounded-md flex items-center gap-1">
                              En Auditoría
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Title & Scope */}
                      <div className="mt-3">
                        <h4 className="font-bold text-white text-sm leading-snug group-hover:text-amber-300 transition-colors">
                          {cert.name}
                        </h4>
                        <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                          {cert.scope}
                        </p>
                      </div>

                      {/* Issuer & Lead Auditor */}
                      <div className="mt-3 pt-2.5 border-t border-[#232532] text-xs space-y-1">
                        <div className="flex items-center justify-between text-slate-400 text-[11px]">
                          <span>Ente Emisor:</span>
                          <strong className="text-slate-200">{cert.issuer}</strong>
                        </div>
                        <div className="flex items-center justify-between text-slate-400 text-[11px]">
                          <span>Auditor Líder:</span>
                          <span className="text-slate-300">{cert.leadAuditor || 'Por designar'}</span>
                        </div>
                        <div className="flex items-center justify-between text-slate-400 text-[11px] font-mono">
                          <span>Vigencia Límite:</span>
                          <strong className={`font-bold ${v.daysRemaining <= 30 ? 'text-amber-400' : 'text-slate-200'}`}>
                            {cert.validUntil}
                          </strong>
                        </div>
                      </div>

                      {/* Attached Documents Quick Bar */}
                      <div className="mt-3 bg-[#111216] p-2.5 rounded-xl border border-[#232532] space-y-1.5">
                        <div className="flex items-center justify-between text-[10px] text-slate-400 font-semibold">
                          <span className="flex items-center gap-1">
                            <Paperclip className="w-3 h-3 text-slate-400" />
                            <span>Expediente de Archivos ({attachmentsCount})</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => setSelectedCertForHistory(cert)}
                            className="text-indigo-400 hover:text-indigo-300 underline cursor-pointer"
                          >
                            Ver Todos / Subir
                          </button>
                        </div>

                        {attachmentsCount === 0 ? (
                          <p className="text-[10px] text-slate-500 italic">
                            Sin archivos adjuntos aún.
                          </p>
                        ) : (
                          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                            {cert.attachments?.slice(0, 3).map((att) => (
                              <button
                                key={att.id}
                                type="button"
                                onClick={() => handleDownloadAttachment(att, cert.code)}
                                className="px-2 py-1 rounded-lg bg-[#1a1c24] hover:bg-indigo-950 border border-[#2a2c3a] hover:border-indigo-500 text-[10px] text-slate-300 hover:text-white flex items-center gap-1.5 transition-colors cursor-pointer truncate max-w-[150px]"
                                title={`Descargar ${att.name}`}
                              >
                                {renderFileBadge(att.fileType)}
                                <span className="truncate">{att.name}</span>
                                <Download className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                              </button>
                            ))}
                            {attachmentsCount > 3 && (
                              <button
                                type="button"
                                onClick={() => setSelectedCertForHistory(cert)}
                                className="px-1.5 py-1 rounded-lg bg-indigo-950/60 text-indigo-300 text-[10px] font-bold border border-indigo-700/50"
                              >
                                +{attachmentsCount - 3} más
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-2 border-t border-[#232532] flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedCertForHistory(cert)}
                        className="px-3 py-1.5 rounded-xl bg-[#20222d] hover:bg-[#2d3040] text-slate-300 hover:text-white text-xs font-semibold flex items-center gap-1.5 cursor-pointer transition-colors"
                      >
                        <History className="w-3.5 h-3.5 text-indigo-400" />
                        <span>Histórico & Archivos</span>
                      </button>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(cert)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#252836] cursor-pointer transition-colors"
                          title="Editar certificación"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCert(cert.id, cert.name)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-950/40 cursor-pointer transition-colors"
                          title="Eliminar certificación"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 2. TABLE VIEW */}
      {viewMode === 'table' && (
        <div className="bg-[#16171b] rounded-2xl border border-[#282932] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#111216] text-slate-400 font-mono uppercase text-[10px] border-b border-[#282932]">
                <tr>
                  <th className="p-3.5">Código / Norma</th>
                  <th className="p-3.5">Certificación / Auditoría</th>
                  <th className="p-3.5">Categoría</th>
                  <th className="p-3.5">Organismo Emisor</th>
                  <th className="p-3.5">Fecha Vencimiento</th>
                  <th className="p-3.5">Días Restantes</th>
                  <th className="p-3.5">Estatus</th>
                  <th className="p-3.5">Archivos Adjuntos</th>
                  <th className="p-3.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#20222e]">
                {filteredCertificates.map((cert) => {
                  const v = cert.validity;
                  const atts = cert.attachments || [];

                  return (
                    <tr key={cert.id} className="hover:bg-[#1b1c24] transition-colors">
                      <td className="p-3.5 font-mono font-bold text-amber-400 whitespace-nowrap">
                        {cert.code}
                      </td>
                      <td className="p-3.5">
                        <div className="font-bold text-white">{cert.name}</div>
                        <div className="text-[10px] text-slate-400 truncate max-w-xs">{cert.scope}</div>
                      </td>
                      <td className="p-3.5 whitespace-nowrap">
                        <span className="text-[10px] uppercase font-mono bg-[#111216] px-2 py-0.5 rounded text-slate-300 border border-[#272935]">
                          {cert.category}
                        </span>
                      </td>
                      <td className="p-3.5 text-slate-300 whitespace-nowrap">
                        {cert.issuer}
                      </td>
                      <td className="p-3.5 font-mono text-white whitespace-nowrap font-bold">
                        {cert.validUntil}
                      </td>
                      <td className="p-3.5 font-mono whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded font-bold ${
                          v.daysRemaining < 0
                            ? 'bg-red-950 text-red-300 border border-red-700'
                            : v.daysRemaining <= 30
                            ? 'bg-amber-950 text-amber-300 border border-amber-700 animate-pulse'
                            : 'text-emerald-400'
                        }`}>
                          {v.daysRemaining} días
                        </span>
                      </td>
                      <td className="p-3.5 whitespace-nowrap">
                        {v.computedStatus === 'vencido' && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-950 text-red-300 border border-red-700">
                            Vencida
                          </span>
                        )}
                        {v.computedStatus === 'proximo_vencer' && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-950 text-amber-300 border border-amber-700">
                            Por Vencer (≤30d)
                          </span>
                        )}
                        {v.computedStatus === 'vigente' && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-950 text-emerald-300 border border-emerald-700">
                            Vigente
                          </span>
                        )}
                        {v.computedStatus === 'en_auditoria' && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-950 text-sky-300 border border-sky-700">
                            En Auditoría
                          </span>
                        )}
                      </td>
                      <td className="p-3.5 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedCertForHistory(cert)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#111216] hover:bg-indigo-950 text-indigo-300 border border-[#272935] hover:border-indigo-500 font-mono text-[11px] cursor-pointer"
                        >
                          <Paperclip className="w-3 h-3 text-indigo-400" />
                          <span>{atts.length} archivo(s)</span>
                        </button>
                      </td>
                      <td className="p-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedCertForHistory(cert)}
                            className="p-1.5 rounded-lg text-indigo-400 hover:text-white hover:bg-indigo-950 cursor-pointer"
                            title="Ver expediente e histórico"
                          >
                            <History className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenEditModal(cert)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#252836] cursor-pointer"
                            title="Editar"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCert(cert.id, cert.name)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-950/50 cursor-pointer"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. CENTRAL REPOSITORY OF ATTACHED FILES VIEW */}
      {viewMode === 'files' && (
        <div className="bg-[#16171b] rounded-2xl border border-[#282932] p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-[#282932] pb-3">
            <div>
              <h4 className="font-bold text-white text-sm flex items-center gap-2">
                <FolderArchive className="w-4 h-4 text-indigo-400" />
                <span>Expediente Digital Consolidado de Documentos y Evidencias</span>
              </h4>
              <p className="text-xs text-slate-400">
                Descargue directamente cualquier archivo (PDF, PowerPoint, Word, Imagen) adjuntado a los sistemas de gestión.
              </p>
            </div>
            <span className="text-xs font-mono font-bold text-indigo-300 bg-indigo-950/80 px-2.5 py-1 rounded-xl border border-indigo-700/50">
              {metrics.totalFiles} Documentos en Total
            </span>
          </div>

          {metrics.totalFiles === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No hay documentos adjuntos en el catálogo general. Abra cualquier certificación para cargar sus evidencias.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {enrichedCertificates.flatMap(cert => 
                (cert.attachments || []).map(att => ({
                  ...att,
                  certCode: cert.code,
                  certName: cert.name,
                  certValidUntil: cert.validUntil
                }))
              ).map(item => (
                <div 
                  key={item.id}
                  className="p-3.5 rounded-xl bg-[#111216] border border-[#272935] hover:border-indigo-500/50 flex flex-col justify-between gap-3 shadow-md group"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="p-2 rounded-lg bg-[#181920] border border-[#2d2f3e] shrink-0">
                      {renderFileBadge(item.fileType)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono font-bold text-amber-400 bg-amber-950/50 px-1.5 py-0.2 rounded">
                          {item.certCode}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {item.sizeFormatted || 'N/A'}
                        </span>
                      </div>
                      <h5 className="font-bold text-white text-xs mt-1 truncate" title={item.name}>
                        {item.name}
                      </h5>
                      <p className="text-[10px] text-slate-400 truncate">
                        Norma: {item.certName}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-[#232532] flex items-center justify-between text-[10px] text-slate-500">
                    <span>Subido: {item.uploadedAt ? item.uploadedAt.split('T')[0] : 'Reciente'}</span>
                    <button
                      onClick={() => handleDownloadAttachment(item, item.certCode)}
                      className="px-2.5 py-1 rounded-lg bg-indigo-600/30 hover:bg-indigo-600 text-indigo-300 hover:text-white font-semibold flex items-center gap-1 transition-colors cursor-pointer border border-indigo-500/40"
                    >
                      <Download className="w-3 h-3" />
                      <span>Descargar</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CREATE & EDIT MODAL */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-[#14151a] border border-[#2d2f3d] rounded-2xl max-w-2xl w-full text-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh] my-auto">
            
            <div className="p-4 sm:p-5 bg-[#1a1c24] border-b border-[#2d2f3d] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400 flex items-center justify-center">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-white">
                    {editingCert ? 'Editar Certificación / Auditoría' : 'Registrar Nueva Certificación / Auditoría'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Capture los datos normativos, fechas de vigencia y adjunte los archivos de evidencia.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#252836]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveForm} className="p-4 sm:p-5 overflow-y-auto space-y-4 text-xs flex-1">
              
              {/* Row 1: Code & Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-300 font-semibold block mb-1">
                    Código / Norma Internacional: *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: ISO 9001:2015, ISO 14001, IATF 16949..."
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold block mb-1">
                    Categoría de Acreditación: *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                    className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                  >
                    <option value="calidad">Calidad (ISO 9001 / IATF 16949)</option>
                    <option value="ambiental">Medio Ambiente (ISO 14001)</option>
                    <option value="seguridad">Seguridad y Salud (ISO 45001 / STPS)</option>
                    <option value="proteccion_civil">Protección Civil / Dictamen Bomberos</option>
                    <option value="metrologia">Metrología y Calibración</option>
                    <option value="auditoria_cliente">Auditoría de Cliente (VDA / OEM)</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Name */}
              <div>
                <label className="text-slate-300 font-semibold block mb-1">
                  Nombre de la Certificación / Acreditación: *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Sistema de Gestión de la Calidad para Manufactura y Logística Automotriz..."
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* Row 3: Issuer & Lead Auditor */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-300 font-semibold block mb-1">
                    Organismo Certificador / Emisor: *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: TÜV Rheinland, SGS, BSI, AENOR, STPS..."
                    value={formData.issuer}
                    onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="text-slate-300 font-semibold block mb-1">
                    Auditor Líder / Responsable HSEQ:
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Ing. Pedro Borja / Auditor Externo..."
                    value={formData.leadAuditor}
                    onChange={(e) => setFormData({ ...formData, leadAuditor: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              {/* Row 4: Issue Date & Expiry Date (CRITICAL) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 bg-amber-950/20 rounded-xl border border-amber-500/30">
                <div>
                  <label className="text-slate-300 font-semibold block mb-1">
                    Fecha de Emisión / Última Auditoría: *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.issueDate}
                    onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                    className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="text-amber-300 font-bold block mb-1 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    <span>Fecha de Vencimiento / Próxima Auditoría: *</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    className="w-full bg-[#101115] border border-amber-500/60 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-400 font-bold"
                  />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    El sistema notificará automáticamente 30 días antes de esta fecha.
                  </span>
                </div>
              </div>

              {/* Row 5: Scope */}
              <div>
                <label className="text-slate-300 font-semibold block mb-1">
                  Alcance de la Certificación / Procesos Evaluados:
                </label>
                <textarea
                  rows={2}
                  placeholder="Ej: Operaciones logísticas, transporte de componentes, almacén y distribución..."
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full bg-[#101115] border border-[#2c2e3d] rounded-xl px-3 py-2 text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* Row 6: Multi-File Upload Section */}
              <div className="space-y-2 p-3.5 bg-[#181920] rounded-xl border border-[#272936]">
                <div className="flex items-center justify-between">
                  <label className="text-slate-300 font-bold flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5 text-indigo-400" />
                    <span>Archivos Adjuntos (PDF, PowerPoint, Word, Imágenes):</span>
                  </label>
                  <input
                    type="file"
                    ref={newFileInputRef}
                    onChange={handleStageFiles}
                    multiple
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.svg"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => newFileInputRef.current?.click()}
                    className="px-3 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer shadow-md"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Adjuntar Archivos</span>
                  </button>
                </div>

                {stagedFiles.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 border border-dashed border-[#34374a] rounded-lg">
                    No ha adjuntado archivos. Haga clic en "Adjuntar Archivos" para subir PDF, Word, PowerPoint o imágenes.
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {stagedFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-2 rounded-lg bg-[#111216] border border-[#272935]">
                        <div className="flex items-center gap-2 truncate">
                          {renderFileBadge(file.fileType)}
                          <span className="text-white text-xs truncate">{file.name}</span>
                          <span className="text-slate-400 text-[10px] font-mono">({file.sizeFormatted})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveStagedFile(file.id)}
                          className="text-slate-400 hover:text-red-400 p-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[#252836] hover:bg-slate-700 text-slate-300 text-xs font-semibold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-amber-950/50 cursor-pointer"
                >
                  <Check className="w-4 h-4" />
                  <span>Guardar Certificación</span>
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

      {/* EXCEL EXPORT MODAL */}
      <CertExcelExportModal
        isOpen={isExcelExportModalOpen}
        onClose={() => setIsExcelExportModalOpen(false)}
        certificates={certificates}
        costCenters={costCenters}
        activeCostCenter={activeCostCenter}
        initialFilterMode={exportModalInitialPreset}
      />

      {/* FILE HISTORY & VERSIONING MODAL */}
      <CertFilesHistoryModal
        isOpen={!!selectedCertForHistory}
        onClose={() => setSelectedCertForHistory(null)}
        certificate={selectedCertForHistory}
        onSaveCertificate={(updated) => {
          const newList = certificates.map(c => c.id === updated.id ? updated : c);
          onSaveCertificates(newList);
          setSelectedCertForHistory(updated);
        }}
      />

    </div>
  );
};
